// Change Password page
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../atoms";
import { FormField } from "../molecules";
import { AdminLayout } from "../organisms";
import { authService } from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";

export const ChangePassword = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!formData.currentPassword) {
      setError("Current password is required");
      return;
    }

    if (formData.newPassword.length < 8) {
      setError("New password must be at least 8 characters long");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError("New password must be different from current password");
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword(
        formData.currentPassword,
        formData.newPassword,
      );
      setSuccess("Password changed successfully! Please login again.");
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Logout and redirect to login after 2 seconds
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(
        err.message ||
          "Failed to change password. Please check your current password.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Change Password">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 p-8">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-ink">
              Update Your Password
            </h3>
            <p className="text-ink-soft mt-2">
              Enter your current password and choose a new password. You will be
              logged out after successfully changing your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <FormField
              label="Current Password"
              type="password"
              placeholder="Enter current password"
              value={formData.currentPassword}
              onChange={(e) =>
                setFormData({ ...formData, currentPassword: e.target.value })
              }
              required
            />

            <FormField
              label="New Password"
              type="password"
              placeholder="Enter new password (min 8 characters)"
              value={formData.newPassword}
              onChange={(e) =>
                setFormData({ ...formData, newPassword: e.target.value })
              }
              required
            />

            <FormField
              label="Confirm New Password"
              type="password"
              placeholder="Confirm new password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
              required
            />

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                {success}
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                className="flex-1"
              >
                {loading ? "Changing Password..." : "Change Password"}
              </Button>
              <Link to="/admin" className="flex-1">
                <Button variant="secondary" className="w-full">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </div>

        <div className="bg-terracotta-50 border border-terracotta-100/60 rounded-2xl p-4">
          <h4 className="font-semibold text-terracotta-900 mb-2">
            Password Requirements:
          </h4>
          <ul className="text-sm text-ink-soft space-y-1">
            <li>• At least 8 characters long</li>
            <li>• Different from your current password</li>
            <li>• Both new password fields must match</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};
